import { PROJECT_FORM_TYPES } from "../projectFormStore";
import { PROJECT_TYPES } from "SharedStores/Project/projectStore";
import { FORM_TYPES } from "SharedStores/Form/formStore";
import { ContactRequiredSubfields } from "../../Elements/contactRequiredSubfields";
import { SUFFIXES } from "../../Elements/_elementBase";
import { pvSystemController } from "./pvSystemElementController";
import { energyStorageSystemController } from "./energyStorageSystemElementController";
import { incentiveSelectorController } from "./incentiveSelectorElementController";
import { ENVELOPE_STATUS } from "ESignatures/eSignatureConstants";

function alwaysComplete() {
    return true;
}

function getBooleanPropValue(element, propName) {
    const propVal = element.Properties[propName] || "";
    return propVal.toLowerCase() === "true";
}

function getProjectValForSuffix(element, getters, suffix) {
    const dataFieldId = element.GroupDataFields[suffix];
    const projVal = getters[PROJECT_TYPES.getters.dataFieldValueFromId](dataFieldId);
    return projVal;
}

function genericIsComplete(element, getters) {
    if (!getBooleanPropValue(element, "required")) {
        return true;
    }
    const projVal = getters[PROJECT_TYPES.getters.dataFieldValueFromId](element.DataFieldId);
    return Boolean(projVal);
}
function checkboxIsComplete(element, getters) {
    if (!getBooleanPropValue(element, "required")) {
        return true;
    }
    const projVal = getters[PROJECT_TYPES.getters.dataFieldValueFromId](element.DataFieldId) || "";
    return projVal.toLowerCase() === "true";
}
function suffixIsComplete(element, getters, suffixes) {
    return suffixes.every((suffix) => Boolean(getProjectValForSuffix(element, getters, suffix)));
}
function nameIsComplete(element, getters) {
    if (!getBooleanPropValue(element, "required")) {
        return true;
    }
    const suffixes = [SUFFIXES.Name.First, SUFFIXES.Name.Last];
    return suffixIsComplete(element, getters, suffixes);
}
function getRequiredAddressSuffixes(element) {
    const suffixes = [SUFFIXES.Address.Line1, SUFFIXES.Address.City, SUFFIXES.Address.State, SUFFIXES.Address.ZipCode];
    if (getBooleanPropValue(element, "showcounty")) {
        suffixes.push(SUFFIXES.Address.County);
    }
    return suffixes;
}
function addressIsComplete(element, getters) {
    if (!getBooleanPropValue(element, "required")) {
        return true;
    }
    const suffixes = getRequiredAddressSuffixes(element);
    return suffixIsComplete(element, getters, suffixes);
}

function contactIsComplete(element, getters) {
    const requiredFields = new ContactRequiredSubfields(element.Properties.required);
    if (!requiredFields.hasRequiredSubfields()) {
        return true;
    }
    let suffixes = [];
    if (requiredFields.Address) {
        suffixes = [...getRequiredAddressSuffixes(element)];
    }
    if (requiredFields.Company) {
        suffixes.push(SUFFIXES.Contact.Company);
    }
    if (requiredFields.Email) {
        suffixes.push(SUFFIXES.Contact.Email);
    }
    if (requiredFields.Name) {
        suffixes.push(SUFFIXES.Contact.First);
        suffixes.push(SUFFIXES.Contact.Last);
    }
    if (requiredFields.Phone) {
        suffixes.push(SUFFIXES.Contact.Phone);
    }

    return suffixIsComplete(element, getters, suffixes);
}

function isTableComplete(element, getters) {
    const required = element.Properties.required;
    if (!required) {
        return true;
    }

    const reqColumns = required.split(",");
    let result = true;

    for (let i = 0; i < reqColumns.length && result; i++) {
        const id = reqColumns[i];
        if (!id) {
            continue;
        }

        const multiVal = getters[PROJECT_TYPES.getters.dataFieldMultiValueFromId](id);
        if (multiVal.includes("") || multiVal.includes(null)) {
            result = false;
        }
    }

    return result;
}

function attachmentIsComplete(element, getters) {
    if (!getBooleanPropValue(element, "required")) {
        return true;
    }

    const attachmentVal = getters[PROJECT_TYPES.getters.attachmentFromId](element.AttachmentId);
    return Boolean(attachmentVal);
}

function sensitiveDataIsComplete(element, getters) {
    if (!getBooleanPropValue(element, "required")) {
        return true;
    }

    return suffixIsComplete(element, getters, [SUFFIXES.SensitiveData.Obfuscated]);
}

function customListIsComplete(element, getters) {
    if (!getBooleanPropValue(element, "required")) {
        return true;
    }

    const suffixes = [SUFFIXES.CustomList.RowId];
    if (element.AuxiliaryModel.ExternalDataSchema.CountField != null) {
        suffixes.push(SUFFIXES.CustomList.Count);
    }

    return suffixIsComplete(element, getters, suffixes);
}

function envelopeIsComplete(element, getters) {
    if (!getBooleanPropValue(element, "required")) {
        return true;
    }

    const envelope = getters[PROJECT_TYPES.getters.getEnvelopeFromDefinitionId](element.EnvelopeId);
    return envelope && envelope.Status.Code === ENVELOPE_STATUS.Completed;
}

const elementIsCompleteDictionary = {
    IntegerElement: genericIsComplete,
    DecimalElement: genericIsComplete,
    PriceElement: genericIsComplete,
    DateElement: genericIsComplete,
    NameElement: nameIsComplete,
    AddressElement: addressIsComplete,
    ContactElement: contactIsComplete,
    TextBoxElement: genericIsComplete,
    TextAreaElement: genericIsComplete,
    LiteralElement: alwaysComplete,
    DropDownListElement: genericIsComplete,
    RadioButtonListElement: genericIsComplete,
    CheckBoxElement: checkboxIsComplete,
    EmailElement: genericIsComplete,
    FormulaResultElement: alwaysComplete,
    HyperlinkBuilderElement: alwaysComplete,
    AdminSelectorElement: genericIsComplete,
    PVSystemElement: pvSystemController.isComplete,
    EnergyStorageSystemElement: energyStorageSystemController.isComplete,
    IncentiveSelectorElement: incentiveSelectorController.isComplete,
    DocumentGenerationElement: alwaysComplete,
    AttachmentElement: attachmentIsComplete,
    SignatureRequestElement: alwaysComplete,
    SummationElement: alwaysComplete,
    SensitiveDataElement: sensitiveDataIsComplete,
    ExternalDataSelectorElement: customListIsComplete,
    WebAdapterElement: alwaysComplete,
    TableElement: isTableComplete,
    ImageElement: alwaysComplete,
    EnvelopeElement: envelopeIsComplete,
    ProjectTableElement: isTableComplete,
    RatingElement: genericIsComplete,
    LocationElement: genericIsComplete
};

export function isElementComplete(element, getters) {
    const isCompleteFunc = elementIsCompleteDictionary[element.ClassName];
    if (!isCompleteFunc || typeof isCompleteFunc !== "function") {
        throw new Error(`Could not retrieve isComplete function for element with classname: ${element.ClassName}`);
    }
    return isCompleteFunc(element, getters);
}
export function isPageComplete(page, getters) {
    if (!page.FormElements) {
        return false;
    }
    const visibleProjectElements = page.FormElements.map((elementId) =>
        getters[PROJECT_FORM_TYPES.getters.projectElementFromId](elementId)
    ).filter((projectElement) => projectElement.IsVisible);
    return visibleProjectElements.every((pe) =>
        isElementComplete(getters[FORM_TYPES.getters.elementFromId](pe.FormElementId), getters)
    );
}
